import type { Node as RCNode } from 'react-checkbox-tree'

const countLeaves = (node: RCNode): number => {
  if (!node.children) {
    return 1
  }

  return node.children.reduce((acc, child) => acc + countLeaves(child), 0)
}

function PagesBadge({ node }: { node: RCNode }) {
  const nPages = countLeaves(node)
  if (!node.children && !nPages) {
    return null
  }

  return (
    <span className="text-xs text-blue-11">
      {nPages} {nPages === 1 ? 'page' : 'pages'}
    </span>
  )
}

function addIcons(node: RCNode): RCNode {
  return {
    ...node,
    icon: node.children ? <PagesBadge node={node} /> : undefined,
    children: node.children?.map(addIcons),
  }
}

const buildTree = (urls: string[], shouldAddIcons = true): RCNode[] => {
  if (!urls.length) {
    return [{ label: '_', value: '_', children: [] }]
  }

  urls.sort()
  const urlObjects = urls.map((url) => new URL(url))

  // Group URLs by origin
  const urlsByOrigin = new Map<string, URL[]>()
  for (const url of urlObjects) {
    const origin = url.origin
    if (!urlsByOrigin.has(origin)) {
      urlsByOrigin.set(origin, [])
    }
    urlsByOrigin.get(origin)!.push(url)
  }

  const rootNodes: RCNode[] = []

  for (const [origin, originUrls] of urlsByOrigin) {
    const rootNode: RCNode = {
      label: origin,
      value: origin,
      children: [{ label: '/', value: `${origin}/`, children: undefined }],
    }

    for (const [urlIndex, url] of originUrls.entries()) {
      const pathParts = url.pathname.split('/').filter(Boolean)
      let currentNode = rootNode

      for (const [partIndex, part] of pathParts.entries()) {
        const child = currentNode.children?.find((child) => child.label === `/${part}`)
        if (child) {
          currentNode = child
        } else {
          const isIndexPage =
            part === pathParts[pathParts.length - 1] &&
            // because the URLs are sorted, the next URL will be a child of the current node or the root
            originUrls[urlIndex + 1]?.pathname.split('/').filter(Boolean).includes(part)

          if (currentNode.value.includes(part)) {
            continue
          }
          const value = url.origin + '/' + pathParts.slice(0, partIndex + 1).join('/')
          const newChild: RCNode = {
            label: `/${part}`,
            value: value + (isIndexPage ? '/' : ''),
            children: isIndexPage
              ? [
                  {
                    label: '/',
                    value,
                  },
                ]
              : undefined,
          }
          if (!currentNode.children) {
            currentNode.children = []
          }
          currentNode.children.push(newChild)
          currentNode = newChild
        }
      }

      if (url.search) {
        const queryChild: RCNode = {
          label: `?${url.search.slice(1)}`,
          value: url.href,
          children: undefined,
        }
        currentNode.children = currentNode.children || []
        currentNode.children.push(queryChild)
      }
    }

    if (shouldAddIcons) {
      rootNodes.push(addIcons(rootNode))
    } else {
      rootNodes.push(rootNode)
    }
  }

  return rootNodes
}

export { buildTree }
